import { useState } from 'react'
import type { Category } from '@domain/finance'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { EscolherCategoria } from './EscolherCategoria'

/**
 * O gatilho da categoria: mostra a escolhida e abre a tela cheia.
 *
 * Espelha o `SeletorDeFamilia` de propósito — os dois campos ficam a dois
 * cliques um do outro no cadastro de produto, e um abrindo a roda do sistema
 * enquanto o outro abre um painel com busca seriam duas respostas para a mesma
 * pergunta.
 *
 * ## Ele saiu do cadastro de produto e passou a valer no DETALHE também
 *
 * A decisão 250b deixou o `<select>` nativo em cinco lugares, e o argumento era
 * de **linha**: *"ali a escolha é um detalhe de uma linha entre dezenas, e abrir
 * a tela inteira para cada uma tiraria de vista a lista que se está varrendo"*.
 * Ele continua valendo para a lista de transações, o detalhamento e o classificar — e
 * **não** vale para o detalhe de uma transação: ali a categoria é um campo de
 * ficha, sozinho na tela, e o compositor do detalhamento é um só, não um por
 * item. Era o mesmo caso do cadastro de produto, com a roda do sistema.
 *
 * **E a lista de embalagens em Produtos e a linha da nota também o usam**
 * (decisões 286 e 287): medido, o painel fecha ao escolher e a lista continua
 * montada embaixo, na mesma altura — o "tirar de vista" daquele argumento não
 * acontece.
 *
 * ## Criar daqui grava e entra, sem esperar o caminho
 *
 * O `path` é cunhado por uma Cloud Function depois do `create` (decisão 249), e
 * o produto só precisa do **id** para apontar. Esperar o caminho aqui seria
 * segurar o formulário por causa de um campo que ele nem grava.
 */
/**
 * A cor de cada procedência. Borda e texto, e não fundo: a pílula mora dentro
 * de uma linha de lista, e um fundo cheio por linha viraria uma parede colorida
 * em vinte transações seguidas.
 */
const CORES_DO_TOM: Record<'neutro' | 'pendente' | 'automatico', { borderColor: string; color: string }> = {
  neutro: { borderColor: 'var(--border)', color: 'inherit' },
  pendente: { borderColor: 'var(--pendente)', color: 'var(--pendente)' },
  automatico: { borderColor: 'var(--automatico)', color: 'var(--automatico)' },
}

export function SeletorDeCategoria({
  categorias,
  value,
  onChange,
  onCriou,
  rotulo,
  disabled = false,
  className = 'w-full',
  compacto = false,
  tom = 'neutro',
}: {
  categorias: Category[]
  value: string | null
  onChange: (categoryId: string | null) => void
  /** Uma categoria nova existe — quem carrega a lista que releia. */
  onCriou: () => void
  /**
   * O nome acessível, quando há mais de um seletor na tela. Sem ele o rótulo é
   * o estado ("Categoria: Mercado"), que basta enquanto o campo é único — dois
   * com o mesmo nome é o defeito que a decisão 246e conserta em outro lugar.
   */
  rotulo?: string
  disabled?: boolean
  /** A largura. Cheia num formulário, curta ao lado de um campo de valor. */
  className?: string
  /**
   * A pílula fina, para dentro de uma linha de lista.
   *
   * **Fina é a tinta, e não o alvo.** O botão continua com os 44 px de `alvo`
   * (decisões 207 e 208), e quem encolhe é a pílula pintada dentro dele — o
   * mesmo arranjo das abas de Produtos. A margem negativa devolve ao layout só
   * a altura da pílula, e a sobra do toque avança sobre o respiro da linha.
   */
  compacto?: boolean
  /**
   * A cor da pílula compacta, que diz a PROCEDÊNCIA da categoria (Fase 10):
   * `pendente` sem categoria, `automatico` posta pela tabela de regras e ainda
   * não conferida, `neutro` quando alguém decidiu ou já conferiu.
   *
   * A cor mora aqui, e não numa classe de quem chama, porque ela é sobre o
   * MESMO estado que o seletor mostra — quem troca a categoria troca a cor, e
   * duas fontes para isso divergiriam no primeiro ajuste.
   */
  tom?: 'neutro' | 'pendente' | 'automatico'
}) {
  const { categoryRepository } = useFinanceRepositories()
  const [escolhendo, setEscolhendo] = useState(false)

  const atual = categorias.find((c) => c.id === value) ?? null

  return (
    <>
      {compacto ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setEscolhendo(true)
          }}
          aria-label={rotulo ?? `Categoria: ${atual?.name ?? 'sem categoria'}`}
          className={`alvo -my-2.5 justify-start text-left disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
        >
          <span
            className="inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs"
            style={CORES_DO_TOM[tom]}
          >
            <span className="truncate">{atual?.name ?? 'Sem categoria'}</span>
            <span aria-hidden="true" className="shrink-0 text-[var(--text-faint)]">
              ⌄
            </span>
          </span>
        </button>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setEscolhendo(true)
          }}
          aria-label={rotulo ?? `Categoria: ${atual?.name ?? 'sem categoria'}`}
          className={`alvo justify-between gap-2 rounded-lg border border-[var(--border)] px-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
        >
          <span className="truncate">{atual?.name ?? 'Sem categoria'}</span>
          <span aria-hidden="true" className="shrink-0 text-[var(--text-faint)]">
            ⌄
          </span>
        </button>
      )}

      {escolhendo && (
        <EscolherCategoria
          atual={value}
          categorias={categorias}
          onEscolher={(id) => {
            onChange(id)
            setEscolhendo(false)
          }}
          onCriar={(nome) => {
            void categoryRepository
              .create({
                name: nome,
                parentId: null,
                // Raiz e despesa: `kind` é herdado do pai, e sem pai é preciso
                // escolher — produto que se compra é despesa. Ver o cabeçalho
                // de `EscolherCategoria`.
                kind: 'expense',
                icon: 'tag',
                color: '#7c3aed',
              })
              .then((id) => {
                onChange(id)
                onCriou()
                setEscolhendo(false)
              })
              .catch((erro: unknown) => {
                console.error('SeletorDeCategoria: não deu para criar', erro)
              })
          }}
          onFechar={() => {
            setEscolhendo(false)
          }}
        />
      )}
    </>
  )
}
